import { dynamicFormApiConnector } from 'apiConnector/form/dynamic';
import { withTabFreeze } from 'epic/utils/tabFreeze';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import type { DynamicFormResult } from 'models/forms/DynamicFormResult';
import { isProductSwitch } from 'models/forms/EnrichFormRequest';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil, timeoutWith, withLatestFrom } from 'rxjs/operators';
import { dynamicFormEnrichmentReceivedThunk } from 'state/dynamic/dynamicForm.thunks';
import type { DataById } from 'state/formData/formData.model';
import { commonFormActions } from 'state/forms/actions/form';
import type { EpicWithThunk } from 'state/state.common';
import type { TabType } from 'state/workspaces/model';
import { getTabType, usesDynamicRFQEnrichment } from 'state/workspaces/tabs/tabs.selector';

interface EnrichFormRequest {
  tabId: string;
  negotiationId?: string;
  version: number;
  productType: string;
  productSubType: string;
  wrapper: string;
  variationParameters: DataById;
  rfq: DataById | undefined;
  tabType?: TabType;
}

export interface DynamicFormEnrichmentRequestedEpicApi {
  enrichForm(request: EnrichFormRequest): Observable<DynamicFormResult>;
}

export function dynamicFormEnrichmentRequestedEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (
    action$,
    state$,
    {
      oldHttpClient,
      frontLogger,
      selectors: { getFormEnrichedData, getFormPendingEnrichmentData, getDynamicFormInfos },
    },
  ) =>
    action$.pipe(
      ofType('FORM_ENRICHMENT_REQUESTED'),
      withLatestFrom(state$),
      filter(([{ tabId }, state]) => usesDynamicRFQEnrichment(state, tabId)),
      mergeMap(([{ tabId }, state]) => {
        const { negotiationId, version, sendWholeFormData } = getDynamicFormInfos(state, tabId)!;

        const enrichedData = getFormEnrichedData(state, tabId);
        const pendingData = getFormPendingEnrichmentData(state, tabId);
        const tabType = getTabType(state, tabId);
        const request: EnrichFormRequest = {
          tabId,
          negotiationId,
          version,
          productType: (pendingData.ProductType || enrichedData.ProductType) as string,
          productSubType: (pendingData.ProductSubtype || enrichedData.ProductSubtype) as string,
          wrapper: (pendingData.Wrapper || enrichedData.Wrapper) as string,
          variationParameters: pendingData,
          rfq: sendWholeFormData ? enrichedData : undefined,
          tabType,
        };

        const tabRemoved$ = tabRemoved(action$, tabId, state$);

        const result$ = dynamicFormApiConnector(oldHttpClient)
          .enrichForm(request)
          .pipe(
            takeUntil(tabRemoved$),
            map(
              ({
                values,
                blocksMetadata,
                fieldsMetadata,
                productDescription,
                version: newVersion,
                actions,
                quoteQuery,
                errors,
                allowedRequests,
              }) =>
                dynamicFormEnrichmentReceivedThunk(
                  tabId,
                  blocksMetadata,
                  fieldsMetadata,
                  productDescription,
                  values,
                  newVersion,
                  actions,
                  quoteQuery,
                  allowedRequests,
                  errors,
                ),
            ),
            timeoutWith(timeoutInMillis, throwError(new Error('timeout')), appScheduler),
            catchError(error => {
              frontLogger.error(
                'Enrichment failure detected on dynamic screen. Session linked to negotiation id {negotiationId} will be rollbacked {@FrontError}',
                negotiationId,
                error,
              );
              return of(commonFormActions.fetchFormEnrichmentFailed('dynamic', tabId, error));
            }),
          );

        return isProductSwitch(request) ? withTabFreeze(result$, tabId, 'dynamic') : result$;
      }),
    );
}
